import { Injectable } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable, take, combineLatest, map, takeUntil, filter, startWith, pairwise, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { isMobile } from '@core/utils';
import { FinancialInstitution, LoaderState } from '@store/core/core.interface';
import { IUserProfile } from '@store/common/user.interface';
import { selectFinancialInstitutions } from '@store/core/core.selectors';
import { selectLinkedFIIds, selectUserProfile, selectUserProfileRef } from '@store/user/user.selectors';
import { selectFI } from '@store/core/core.actions';
import { selectConsentId } from '@store/checkout/checkout.selectors';
import { QUERY_PARAMS_KEYS } from '@core/constants/query-params';
import { ToastService } from './toast.service';
import {
  LinkBankSheetComponent,
  LinkBankSheetData,
} from '@checkout/shared/components/link-bank-sheet/link-bank-sheet.component';

@Injectable({
  providedIn: 'root',
})
export class LinkBankService {
  private readonly isMobile = isMobile();
  private readonly financialInstitutions$ = this.store.select(selectFinancialInstitutions);
  private readonly userProfileRef$ = this.store.select(selectUserProfileRef);
  private hasShownSuccessToast = false;

  constructor(
    private readonly store: Store,
    private readonly _bottomSheet: MatBottomSheet,
    private readonly dialog: MatDialog,
    private readonly translateService: TranslateService,
    private readonly toastService: ToastService
  ) {}

  openLinkBankSheet(
    userProfile$: Observable<LoaderState<IUserProfile> | undefined>,
    redirectUrl: string,
    event?: Event
  ): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    userProfile$.pipe(take(1)).subscribe((userProfile) => {
      if (!userProfile?.data?.linked_fi_info) {
        return;
      }

      combineLatest([this.financialInstitutions$, this.store.select(selectLinkedFIIds)])
        .pipe(
          take(1),
          map(([financialInstitutions, linkedFIIds]) => {
            return {
              financialInstitutions,
              linkedFIIds,
            } as LinkBankSheetData;
          })
        )
        .subscribe((data) => {
          if (this.isMobile) {
            const bottomSheetRef = this._bottomSheet.open(LinkBankSheetComponent, {
              data,
              panelClass: 'content_main-drawer',
              backdropClass: 'content_main-bottom-sheet',
              restoreFocus: false,
              autoFocus: false,
              disableClose: false,
              hasBackdrop: true,
            });

            combineLatest([bottomSheetRef.afterDismissed(), this.userProfileRef$]).subscribe(
              ([selectedFI, userProfileRef]) => {
                if (selectedFI) {
                  this.fiSelected(selectedFI, userProfileRef, redirectUrl);
                }
              }
            );
          } else {
            const dialogRef = this.dialog.open(LinkBankSheetComponent, {
              data,
              closeOnNavigation: true,
              autoFocus: false,
              width: '416px',
              panelClass: 'content_overlay-medium',
              backdropClass: 'content_overlay-dialog',
              disableClose: false,
              hasBackdrop: true,
            });

            combineLatest([dialogRef.afterClosed(), this.userProfileRef$]).subscribe(([selectedFI, userProfileRef]) => {
              if (selectedFI) {
                this.fiSelected(selectedFI, userProfileRef, redirectUrl);
              }
            });
          }
        });
    });
  }

  handleFILinkSuccess(destroy$: Subject<boolean>): void {
    const params = new URLSearchParams(window.location.search);
    const userRef = params.get(QUERY_PARAMS_KEYS.userRef);
    const fiUserId = params.get(QUERY_PARAMS_KEYS.fiUserId);
    const stepUp = params.get('stepUp') || params.get('stepupCompleted');

    if (userRef && fiUserId) {
      const url = window.location.pathname;
      window.history.replaceState({}, document.title, url);

      if (stepUp !== 'true') {
        this.store
          .select(selectUserProfile)
          .pipe(
            takeUntil(destroy$),
            filter((userProfile) => !!userProfile?.data?.linked_fi_info),
            startWith(null),
            pairwise(),
            filter(([prev, curr]) => !!curr?.data && !this.hasShownSuccessToast),
            take(1)
          )
          .subscribe(([prev, curr]) => {
            const prevCount = prev?.data?.linked_fi_info?.length || 0;
            const currCount = curr?.data?.linked_fi_info?.length || 0;

            if (currCount > prevCount) {
              this.hasShownSuccessToast = true;
              this.translateService
                .get('COMMON.PAYMENT_METHOD_ADDED')
                .pipe(take(1))
                .subscribe((message) => {
                  this.toastService.show(message, 'success');
                });
            }
          });
      }
    }
  }

  resetSuccessToastFlag(): void {
    this.hasShownSuccessToast = false;
  }

  private fiSelected(selectedFI: FinancialInstitution, userProfileRef?: string, redirectUrl?: string) {
    const selectFIPayload: {
      selectedFI: FinancialInstitution;
      redirectUrl: string;
      userProfileRef?: string;
      rus?: boolean;
    } = {
      selectedFI,
      redirectUrl: redirectUrl || window.location.pathname,
      rus: false,
    };

    if (userProfileRef) {
      selectFIPayload.userProfileRef = userProfileRef;
    }

    this.store
      .select(selectConsentId)
      .pipe(take(1))
      .subscribe((consentId) => {
        this.store.dispatch(selectFI({ ...selectFIPayload, consentId }));
      });
  }
}
